import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, Paper, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import tokenStore from '../stores/tokenStore';

const Verify2FAPage: React.FC = () => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenFound, setTokenFound] = useState(false);

  const { loginWithUser } = useAuth();
  const navigate = useNavigate();

  // Controlla se il token esiste al caricamento della pagina
  useEffect(() => {
    const tokenFromStore = tokenStore.getToken();
    const tokenFromStorage = sessionStorage.getItem('2fa_token');
    
    if (tokenFromStore || tokenFromStorage) {
      setTokenFound(true);
    } else {
      setError('Sessione 2FA non trovata. Effettua nuovamente il login.');
      // Reindirizza al login dopo 3 secondi
      setTimeout(() => navigate('/login'), 3000);
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Recupera il token da entrambe le fonti
    const tokenFromStore = tokenStore.getToken();
    const tokenFromStorage = sessionStorage.getItem('2fa_token');
    const tempToken = tokenFromStore || tokenFromStorage;

    if (!tempToken) {
      setError('ERRORE: Token di sessione 2FA non trovato. Ritorno al login...');
      setTimeout(() => navigate('/login'), 2000);
      setLoading(false);
      return;
    }

    try {
      const response = await authService.verify2FA(tempToken, code);

      // Pulizia da entrambi i posti
      tokenStore.clearToken();
      sessionStorage.removeItem('2fa_token');

      loginWithUser(response.user);
      navigate('/');

    } catch (err: any) {
      let errorMessage = 'Errore durante la verifica del codice.';
      
      if (err.response) {
        errorMessage = err.response.data?.error || err.response.data?.message || 'Codice non valido o sessione scaduta.';
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!tokenFound) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Paper elevation={3} sx={{ padding: 4, width: 400, maxWidth: '90%' }}>
          <Typography variant="h5" gutterBottom align="center" color="error">
            Sessione non valida
          </Typography>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
      <Paper elevation={3} sx={{ padding: 4, width: 400, maxWidth: '90%' }}>
        <Typography variant="h5" gutterBottom align="center">
          Verifica Account
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Inserisci il codice a 6 cifre dall'app Microsoft Authenticator
        </Typography>
        
        <form onSubmit={handleSubmit}>
          <TextField 
            label="Codice a 6 cifre" 
            fullWidth 
            margin="normal" 
            value={code} 
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} // Solo numeri, max 6 cifre
            required 
            inputProps={{ 
              maxLength: 6,
              pattern: "[0-9]{6}",
              inputMode: "numeric"
            }}
            placeholder="123456"
          />
          
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          
          <Button 
            type="submit" 
            variant="contained" 
            fullWidth 
            sx={{ mt: 2 }} 
            disabled={loading || code.length !== 6}
          >
            {loading ? <CircularProgress size={24} /> : 'Verifica e Accedi'}
          </Button>
        </form>
        
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button 
            variant="text" 
            onClick={() => navigate('/login')}
            disabled={loading}
          >
            Torna al Login
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Verify2FAPage;